/**
 * FLORALINA IMMERSIVE v4.2.3
 * Unified scroll handler, token-based config, XSS-safe search.
 * No external dependencies.
 */
(function() {
  'use strict';

  /* ── Configuration ── */
  var CONFIG = {
    scrollThreshold: 80,          // px before nav condenses
    heroZoomFactor: 0.08,         // max scale increase on scroll
    heroTextFadeRate: 2.5,        // how fast hero text fades
    heroTextShift: 40,            // max px shift for hero text
    menuCloseDelay: 300,          // ms before menu closes on hover-out
    searchDebounce: 280,          // ms debounce for search input
    searchMinChars: 2,            // min chars before search fires
    curtainDuration: 420,         // ms before navigation after curtain
    counterDuration: 2000,        // ms for count-up animation
    orbParallaxFactor: 0.06       // scroll multiplier for orbs
  };

  /* ── Shared State ── */
  var scrollY = 0;
  var ticking = false;
  var scrollCallbacks = [];

  /* ── Unified Scroll Handler ── */
  function onScroll() {
    scrollY = window.scrollY;
    for (var i = 0; i < scrollCallbacks.length; i++) {
      scrollCallbacks[i](scrollY);
    }
    ticking = false;
  }

  function registerScroll(fn) {
    scrollCallbacks.push(fn);
  }

  function startScrollListener() {
    window.addEventListener('scroll', function() {
      if (!ticking) {
        requestAnimationFrame(onScroll);
        ticking = true;
      }
    }, { passive: true });
  }

  /* ── Nav Scroll ── */
  function initNavScroll() {
    var nav = document.querySelector('.header-wrapper');
    if (!nav) return;
    registerScroll(function(y) {
      if (y > CONFIG.scrollThreshold) {
        nav.classList.add('fl-nav--scrolled');
      } else {
        nav.classList.remove('fl-nav--scrolled');
      }
    });
  }

  /* ── Logo Transform Measurement ── */
  function initLogoTransform() {
    var wrapper = document.querySelector('.header__heading-logo-wrapper');
    if (!wrapper) return;
    var title = wrapper.querySelector('.header__heading-link, .header__heading > a, h1 > a');
    if (!title) title = wrapper.children[0];
    if (!title) return;

    function measure() {
      var w = title.getBoundingClientRect().width;
      wrapper.style.setProperty('--logo-title-width', Math.round(w / 2) + 'px');
    }

    measure();
    window.addEventListener('resize', function() {
      requestAnimationFrame(measure);
    }, { passive: true });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure);
    }
  }

  /* ── Hero Scroll Transformation ── */
  function initHeroParallax() {
    var hero = document.querySelector('.video-hero, [class*="video-hero"]');
    if (!hero) return;

    var media = hero.querySelector('.video-hero__media, video, img');
    var overlay = hero.querySelector('.video-hero__overlay');
    var heading = hero.querySelector('.video-hero__heading');
    var sub = hero.querySelector('.video-hero__subheading');
    var eyebrow = hero.querySelector('.video-hero__eyebrow');
    var buttons = hero.querySelector('.video-hero__buttons');
    var heroH = hero.offsetHeight;

    registerScroll(function(y) {
      if (y > heroH * 1.2) return;
      var progress = Math.min(y / heroH, 1);

      if (media) media.style.transform = 'scale(' + (1 + progress * CONFIG.heroZoomFactor) + ')';
      if (overlay) overlay.style.opacity = 1 + progress * 0.4;

      var textFade = Math.max(1 - progress * CONFIG.heroTextFadeRate, 0);
      var shift = progress * CONFIG.heroTextShift;

      if (heading) { heading.style.transform = 'translateY(-' + shift + 'px)'; heading.style.opacity = textFade; }
      if (sub) { sub.style.transform = 'translateY(-' + (shift * 0.8) + 'px)'; sub.style.opacity = textFade; }
      if (eyebrow) eyebrow.style.opacity = textFade;
      if (buttons) { buttons.style.transform = 'translateY(-' + (shift * 0.6) + 'px)'; buttons.style.opacity = textFade; }
    });
  }

  /* ── Parallax Orbs (now uses unified scroll) ── */
  function initParallax() {
    var orbs = document.querySelectorAll('.fl-parallax-orb');
    if (!orbs.length) return;
    registerScroll(function(y) {
      for (var i = 0; i < orbs.length; i++) {
        orbs[i].style.transform = 'translateY(' + (y * (i + 1) * CONFIG.orbParallaxFactor) + 'px)';
      }
    });
  }

  /* ── Menu Hover-Away Close ── */
  function initMenuHoverClose() {
    var menuItems = document.querySelectorAll('details.mega-menu, header-menu > details, .header__menu-item > details');
    if (!menuItems.length) return;

    menuItems.forEach(function(details) {
      var timer = null;
      var hoverTarget = details.closest('li') || details.closest('header-menu') || details;
      var summary = details.querySelector('summary');

      if (summary) {
        summary.addEventListener('click', function() {
          menuItems.forEach(function(other) {
            if (other !== details && other.open) other.removeAttribute('open');
          });
        });
      }

      hoverTarget.addEventListener('mouseleave', function() {
        timer = setTimeout(function() { details.removeAttribute('open'); }, CONFIG.menuCloseDelay);
      });

      hoverTarget.addEventListener('mouseenter', function() {
        if (timer) { clearTimeout(timer); timer = null; }
      });
    });

    document.addEventListener('click', function(e) {
      if (e.target.closest('details.mega-menu, header-menu, .header__menu-item')) return;
      menuItems.forEach(function(d) { if (d.open) d.removeAttribute('open'); });
    });
  }

  /* ── Inline Search (XSS-safe) ── */
  function initInlineSearch() {
    var searchToggle = document.querySelector('details-modal.header__search');
    if (!searchToggle) return;

    var wrap = document.createElement('div');
    wrap.className = 'fl-inline-search';
    wrap.innerHTML =
      '<div class="fl-inline-search__inner">' +
        '<div class="fl-inline-search__bar">' +
          '<svg class="fl-inline-search__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="21" y2="21"/></svg>' +
          '<input type="text" class="fl-inline-search__input" placeholder="Search arrangements, occasions..." autocomplete="off" />' +
          '<button class="fl-inline-search__close" aria-label="Close search">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="fl-inline-search__results"></div>' +
      '</div>';

    var header = document.querySelector('.header-wrapper');
    if (header) header.appendChild(wrap);

    var input = wrap.querySelector('.fl-inline-search__input');
    var results = wrap.querySelector('.fl-inline-search__results');
    var closeBtn = wrap.querySelector('.fl-inline-search__close');
    var debounce = null;

    function closeSearch() {
      wrap.classList.remove('fl-inline-search--open');
      input.value = '';
      results.innerHTML = '';
    }

    // Intercept search modal
    var btn = searchToggle.querySelector('summary, button, [data-action]');
    if (btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        wrap.classList.add('fl-inline-search--open');
        setTimeout(function() { input.focus(); }, 120);
      });
    }

    closeBtn.addEventListener('click', closeSearch);
    input.addEventListener('keydown', function(e) { if (e.key === 'Escape') closeSearch(); });

    // XSS-safe result rendering
    function escapeHtml(str) {
      var div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    function renderResults(data) {
      var resources = data.resources && data.resources.results;
      if (!resources) { results.innerHTML = '<p class="fl-inline-search__empty">No results</p>'; return; }

      var frag = document.createDocumentFragment();

      function addGroup(label, items, type) {
        if (!items || !items.length) return;
        var group = document.createElement('div');
        group.className = 'fl-inline-search__group';
        var lbl = document.createElement('span');
        lbl.className = 'fl-inline-search__label';
        lbl.textContent = label;
        group.appendChild(lbl);

        items.forEach(function(item) {
          var a = document.createElement('a');
          a.href = item.url;
          a.className = 'fl-inline-search__item' + (type ? ' fl-inline-search__item--' + type : '');

          if (item.image && type !== 'collection' && type !== 'page') {
            var img = document.createElement('img');
            img.src = item.image;
            img.alt = '';
            img.loading = 'lazy';
            a.appendChild(img);
          }

          var info = document.createElement('div');
          var title = document.createElement('span');
          title.className = 'fl-inline-search__title';
          title.textContent = item.title;
          info.appendChild(title);

          if (item.price && type !== 'collection' && type !== 'page') {
            var price = document.createElement('span');
            price.className = 'fl-inline-search__price';
            price.textContent = '$' + parseFloat(item.price).toFixed(2);
            info.appendChild(price);
          }

          a.appendChild(info);
          group.appendChild(a);
        });

        frag.appendChild(group);
      }

      addGroup('Products', resources.products, null);
      addGroup('Collections', resources.collections, 'collection');
      addGroup('Pages', resources.pages, 'page');

      results.innerHTML = '';
      if (frag.children.length) {
        results.appendChild(frag);
      } else {
        results.innerHTML = '<p class="fl-inline-search__empty">No results</p>';
      }
    }

    input.addEventListener('input', function() {
      clearTimeout(debounce);
      var q = input.value.trim();
      if (q.length < CONFIG.searchMinChars) { results.innerHTML = ''; return; }
      debounce = setTimeout(function() {
        fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product,collection,page&resources[limit]=6')
          .then(function(r) { return r.json(); })
          .then(renderResults)
          .catch(function() { results.innerHTML = '<p class="fl-inline-search__empty">Search unavailable</p>'; });
      }, CONFIG.searchDebounce);
    });
  }

  /* ── Cinematic Curtain ── */
  function initCurtain() {
    var curtain = document.createElement('div');
    curtain.className = 'fl-curtain';
    document.body.appendChild(curtain);

    var selectors = [
      'a.header__menu-item',
      'details.mega-menu a',
      '.mega-menu__link',
      '.video-hero__button',
      '.header__heading-link',
      'a.button--primary',
      '.collection-card-wrapper a',
      '.menu-drawer__menu-item a'
    ].join(',');

    function shouldCurtain(el) {
      if (!el || !el.href) return false;
      if (el.hostname !== window.location.hostname) return false;
      if (el.href.indexOf('#') !== -1 && el.pathname === window.location.pathname) return false;
      if (el.closest('form') || el.closest('.cart-drawer') || el.closest('.fl-inline-search')) return false;
      return el.matches(selectors) || !!el.closest(selectors);
    }

    document.addEventListener('click', function(e) {
      var link = e.target.closest('a');
      if (!shouldCurtain(link)) return;
      e.preventDefault();
      var href = link.href;
      curtain.className = 'fl-curtain fl-curtain--entering';
      setTimeout(function() { window.location.href = href; }, CONFIG.curtainDuration);
    });

    if (performance.getEntriesByType && performance.getEntriesByType('navigation').length) {
      var navType = performance.getEntriesByType('navigation')[0].type;
      if (navType === 'navigate' || navType === 'reload') {
        if (sessionStorage.getItem('fl_curtain_active')) {
          sessionStorage.removeItem('fl_curtain_active');
          curtain.style.transform = 'translateY(0)';
          curtain.className = 'fl-curtain fl-curtain--exiting';
          document.body.classList.add('fl-page-entered');
          setTimeout(function() { curtain.className = 'fl-curtain'; curtain.style.transform = ''; }, 500);
        }
      }
    }

    window.addEventListener('beforeunload', function() {
      if (curtain.classList.contains('fl-curtain--entering')) {
        sessionStorage.setItem('fl_curtain_active', '1');
      }
    });
  }

  /* ── Count-Up Animation ── */
  function initCounters() {
    var counters = document.querySelectorAll('[data-fl-count]');
    if (!counters.length) return;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting || entry.target.dataset.flCounted) return;
        entry.target.dataset.flCounted = '1';
        var target = parseInt(entry.target.dataset.flCount, 10);
        var start = performance.now();

        function update(now) {
          var p = Math.min((now - start) / CONFIG.counterDuration, 1);
          var eased = 1 - Math.pow(1 - p, 4);
          entry.target.textContent = Math.round(eased * target).toLocaleString();
          if (p < 1) requestAnimationFrame(update);
        }
        requestAnimationFrame(update);
      });
    }, { threshold: 0.5 });

    counters.forEach(function(c) { observer.observe(c); });
  }

  /* ── Dust Engine ── */
  var DustEngine = {
    canvas: null, ctx: null, particles: [], animating: false,
    init: function() {
      this.canvas = document.getElementById('fl-dust-canvas');
      if (!this.canvas) return;
      this.ctx = this.canvas.getContext('2d');
      this._resize();
      window.addEventListener('resize', this._resize.bind(this));
    },
    _resize: function() {
      if (!this.canvas) return;
      this.canvas.width = window.innerWidth;
      this.canvas.height = window.innerHeight;
    },
    spawn: function(rect, colors) {
      var step = 2;
      for (var x = rect.left; x < rect.right; x += step) {
        for (var y = rect.top; y < rect.bottom; y += step) {
          if (Math.random() > 0.25) {
            var angle = Math.random() * Math.PI * 2;
            var speed = Math.random() * 6 + 2;
            this.particles.push({
              x: x, y: y,
              vx: Math.cos(angle) * speed * (0.5 + Math.random()),
              vy: Math.sin(angle) * speed * (0.5 + Math.random()) - Math.random() * 2,
              size: Math.random() * 1.5 + 0.5,
              gravity: 0.03 + Math.random() * 0.03,
              friction: 0.985,
              opacity: 0.6 + Math.random() * 0.4,
              decay: 0.004 + Math.random() * 0.008,
              color: colors[Math.floor(Math.random() * colors.length)]
            });
          }
        }
      }
      if (!this.animating) this._animate();
    },
    _animate: function() {
      var self = this;
      self.animating = true;
      self.ctx.clearRect(0, 0, self.canvas.width, self.canvas.height);
      self.particles = self.particles.filter(function(p) {
        p.vx *= p.friction; p.vy *= p.friction; p.vy += p.gravity;
        p.x += p.vx; p.y += p.vy; p.opacity -= p.decay;
        if (p.opacity <= 0) return false;
        self.ctx.globalAlpha = p.opacity;
        self.ctx.fillStyle = p.color;
        self.ctx.fillRect(p.x, p.y, p.size, p.size);
        return true;
      });
      if (self.particles.length > 0) {
        requestAnimationFrame(self._animate.bind(self));
      } else {
        self.animating = false;
        self.ctx.clearRect(0, 0, self.canvas.width, self.canvas.height);
      }
    }
  };

  /* ── Initialize ── */
  function init() {
    initNavScroll();
    initLogoTransform();
    initHeroParallax();
    initParallax();
    startScrollListener();  // single listener for all scroll handlers
    initCurtain();
    initCounters();
    initMenuHoverClose();
    initInlineSearch();
    DustEngine.init();
    window.FloralinaDust = DustEngine;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
